import React, { useState } from 'react';
import { Camera, Loader2, MapPin, Send, X } from 'lucide-react';
import { Button } from '../../Button';
import { MobileHeader } from '../MobileHeader';
import { irregularityTypes } from '../../../data/irregularities';
import { images } from '../../../data/images';
import { usePrototype } from '../../../contexts/PrototypeContext';
import type { MobileNav } from '../../../types/mobile';
export function ReportScreen({
  nav
}: {
  nav: MobileNav;
}) {
  const {
    addCase,
    cases
  } = usePrototype();
  const invalid = nav.reportContext === 'invalid';
  const vehicle = invalid ? 'ঢাকা-থ-১৩-৭৮৯০' : 'ঢাকা-থ-১১-১২৩৪';
  const qrId = invalid ? 'CNG-000004' : 'CNG-000001';
  const initial = invalid ? ['মেয়াদোত্তীর্ণ লাইসেন্স', 'মেয়াদোত্তীর্ণ ফিটনেস'] : nav.reportContext === 'unauthorized' ? ['অননুমোদিত চালক'] : [];
  const [selected, setSelected] = useState<string[]>(initial);
  const [comment, setComment] = useState(invalid ? 'গাড়ির ফিটনেস সনদ ও চালকের লাইসেন্স উভয়ের মেয়াদ শেষ। চালক নবায়নের কোনো কাগজ দেখাতে পারেননি।' : '');
  const [photos, setPhotos] = useState<string[]>(invalid ? [images.cng] : []);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const toggle = (t: string) => setSelected(s => s.includes(t) ? s.filter(x => x !== t) : [...s, t]);
  const submit = () => {
    if (selected.length === 0) {
      setError('অন্তত একটি অনিয়মের ধরন নির্বাচন করুন');
      return;
    }
    setError('');
    setSubmitting(true);
    window.setTimeout(() => {
      const id = `IRR-2026-${String(372 + cases.length - 7).padStart(4, '0')}`;
      addCase({
        id,
        type: selected[0],
        typeEn: 'Field Report',
        vehicle,
        qrId,
        driver: invalid ? 'মোঃ নুরুল আমিন' : nav.reportContext === 'unauthorized' ? 'অজ্ঞাত (তালিকাবহির্ভূত)' : 'মোঃ করিম শেখ',
        license: invalid ? 'BRTA-1122334' : '—',
        officer: 'সার্জেন্ট মোঃ কামরুল হাসান',
        bp: 'BP-2587',
        location: 'মিরপুর-১০ গোলচত্বর',
        division: 'মিরপুর',
        date: '১২ মার্চ ২০২৬',
        time: 'সকাল ১০:২৬',
        status: 'নতুন',
        remark: comment || selected.join(', ')
      });
      nav.setLastCaseId(id);
      setSubmitting(false);
      nav.go('reportDone');
    }, 1000);
  };
  return <div className="flex h-full flex-col bg-slate-50" data-id="element-1072" data-mp="2rhpkt9yhkjti">
      <MobileHeader title="অনিয়ম রিপোর্ট" subtitle="স্বয়ংক্রিয় তথ্য পূরণকৃত" onBack={nav.back} data-id="element-1073" data-mp="2vi9jwj2mdxia" />
      <div className="no-scrollbar flex-1 overflow-y-auto p-4" data-id="element-1074" data-mp="30hcq46uc69ru">
        <div className="rounded-xl border border-border bg-white p-3.5" data-id="element-1075" data-mp="30frra50zo4o6">
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2.5 text-xs" data-id="element-1076" data-mp="35947prvil55u">
            <Auto label="গাড়ির নম্বর" value={vehicle} data-id="element-1077" data-mp="3a6w8lbe501m6" />
            <Auto label="QR ID" value={qrId} mono data-id="element-1078" data-mp="3a6e2ln1oa912" />
            <Auto label="তারিখ" value="১২ মার্চ ২০২৬" data-id="element-1079" data-mp="3f6sc79ix9r17" />
            <Auto label="সময়" value="সকাল ১০:২৬" data-id="element-1080" data-mp="3f45wgmlvo57w" />
            <Auto label="অফিসারের নাম" value="সার্জেন্ট মোঃ কামরুল হাসান" data-id="element-1081" data-mp="3j72low5xazkt" />
            <div data-id="element-1082" data-mp="17k8tf92f4dz8">
              <dt className="text-slate-500" data-id="element-1083" data-mp="17kqvimbrhmf2">বর্তমান লোকেশন</dt>
              <dd className="flex items-center gap-1 font-semibold text-navy-900" data-id="element-1084" data-mp="17i5v81r43ejf"><MapPin className="h-3 w-3 text-red-600" data-id="element-1085" data-mp="13hc6j65uhvph" />মিরপুর-১০ গোলচত্বর</dd>
              <dd className="font-mono text-[10px] text-slate-400" data-id="element-1086" data-mp="ykvjkfadtndz">23.8069°N, 90.3687°E</dd>
            </div>
          </dl>
        </div>

        <h2 className="mb-2 mt-4 text-xs font-semibold text-slate-500" data-id="element-1087" data-mp="tmu0hucyho2i">অনিয়মের ধরন নির্বাচন করুন</h2>
        <div className="flex flex-wrap gap-2" role="group" aria-label="অনিয়মের ধরন" data-id="element-1088" data-mp="1raklqvytvth6">
          {irregularityTypes.map(t => {
          const on = selected.includes(t);
          return <button key={t} onClick={() => toggle(t)} aria-pressed={on} className={`rounded-full border px-3 py-1.5 text-xs font-medium ${on ? 'border-red-600 bg-red-600 text-white' : 'border-border bg-white text-slate-700'}`} data-id="element-1089" data-mp="1hglmq5u1wptg">
                {t}
              </button>;
        })}
        </div>
        {error && <p className="mt-2 text-xs text-red-600" data-id="element-1090" data-mp="1pslxp1fxhf6v">{error}</p>}

        <label htmlFor="m-comment" className="mb-1.5 mt-4 block text-xs font-semibold text-slate-500" data-id="element-1091" data-mp="1tsmc4m311qdx">মন্তব্য</label>
        <textarea id="m-comment" value={comment} onChange={e => setComment(e.target.value)} rows={3} placeholder="ঘটনার সংক্ষিপ্ত বিবরণ লিখুন…" className="w-full rounded-lg border border-input bg-white p-3 text-sm outline-none focus:border-navy-500" data-id="element-1092" data-mp="1ftwdh20k7ing" />

        <p className="mb-1.5 mt-3 text-xs font-semibold text-slate-500" data-id="element-1093" data-mp="2dhmxk0ap3i0j">ছবি সংযুক্ত করুন</p>
        <div className="flex gap-2" data-id="element-1094" data-mp="1yotg49bv4rt8">
          {photos.map((p, i) => <div key={i} className="relative h-16 w-16" data-id="element-1095" data-mp="23ofvaob94i4o" data-mpi={i}>
              <img src={p} alt={`সংযুক্ত ছবি ${i + 1}`} className="h-16 w-16 rounded-md object-cover" data-id="element-1096" data-mp="2rf373koafrwi" />
              <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))} aria-label="ছবি সরান" className="absolute -right-1.5 -top-1.5 rounded-full bg-slate-800 p-0.5 text-white" data-id="element-1097" data-mp="2w6tna79wp9vu"><X className="h-3 w-3" data-id="element-1098" data-mp="fs9flzslxabz" /></button>
            </div>)}
          {photos.length < 3 && <button onClick={() => setPhotos([...photos, images.cng])} className="flex h-16 w-16 flex-col items-center justify-center rounded-md border-2 border-dashed border-slate-300 text-[10px] text-slate-500" data-id="element-1099" data-mp="tjeidar9uiqj">
              <Camera className="h-5 w-5" data-id="element-1100" data-mp="1row11evbahf" /> ক্যামেরা
            </button>}
        </div>
      </div>
      <div className="border-t border-border bg-white p-3" data-id="element-1101" data-mp="3sztb4xwtlxqs">
        <Button onClick={submit} disabled={submitting} className="h-11 w-full bg-red-600 hover:bg-red-700" data-id="element-1102" data-mp="at56f95oncln">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-1103" data-mp="5tk72xmlp28e" /> : <Send className="h-4 w-4" data-id="element-1104" data-mp="5sqw1kglzqkg" />}
          {submitting ? 'জমা দেওয়া হচ্ছে…' : 'রিপোর্ট জমা দিন'}
        </Button>
      </div>
    </div>;
}
function Auto({
  label,
  value,
  mono
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return <div data-id="element-1105" data-mp="ewcaxqn6lv0z">
      <dt className="text-slate-500" data-id="element-1106" data-mp="ewm0xe88826o">{label}</dt>
      <dd className={`font-semibold text-navy-900 ${mono ? 'font-mono' : ''}`} data-id="element-1107" data-mp="exx53f5l3byb">{value}</dd>
    </div>;
}